import createSelector from 'lib/web/createSelector'
import { sum } from 'lib/array/arrayUtils'
import { isLuxuryPlusSubscriptionItem } from 'lib/checkout/checkoutUtils'
import { addYears, intlFormatter } from 'lib/datetime/dateUtils'
import { INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { LUXURY_PLUS } from 'loyalty/constants/base'
import { isActiveLuxuryPlusMember } from 'loyalty/selectors/featureToggle'

export const getLuxuryPlusSubscriptionItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.LuxuryPlusSubscriptionItem> => items.filter(isLuxuryPlusSubscriptionItem),
)

export const isStandaloneLuxuryPlusSubscription = createSelector(
  (state: App.State) => state.checkout.cart.items,
  getLuxuryPlusSubscriptionItems,
  (items, luxuryPlusItems): boolean => !!items.length && items.length === luxuryPlusItems.length,
)

export const isLuxuryPlusSubscriptionInCart = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): boolean => items.length > 0 && items.some(isLuxuryPlusSubscriptionItem),
)

export const isMemberOrHasSubscriptionInTheCart = createSelector(
  isLuxuryPlusSubscriptionInCart,
  isActiveLuxuryPlusMember,
  (isLuxuryPlusSubscriptionInCart, isActiveLuxuryPlusMember): boolean => !!(isLuxuryPlusSubscriptionInCart || isActiveLuxuryPlusMember),
)

export const eligibleLuxPlusCheckoutNotGift = createSelector(
  isMemberOrHasSubscriptionInTheCart,
  (state: App.State) => state.checkout.cart.isGift,
  (isMemberOrHasSubscriptionInTheCart, isGift): boolean => isMemberOrHasSubscriptionInTheCart && !isGift,
)

export const checkoutWithMemberPrice = createSelector(
  eligibleLuxPlusCheckoutNotGift,
  isStandaloneLuxuryPlusSubscription,
  (eligibleLuxPlusCheckoutNotGift, isStandaloneLuxuryPlusSubscription): boolean => {
    return eligibleLuxPlusCheckoutNotGift && !isStandaloneLuxuryPlusSubscription
  },
)

export const getLuxuryPlusSubscriptionItemView = createSelector(
  getLuxuryPlusSubscriptionItems,
  (luxuryPlusItems): App.WithDataStatus<Array<App.Checkout.LuxuryPlusSubscriptionItemView>> => {
    return {
      hasRequiredData: true,
      data: luxuryPlusItems.map(item => {
        return {
          itemId: item.itemId,
          price: item.amount,
          taxesAndFees: 0,
          memberPrice: 0,
        }
      }),
    }
  },
)

export const getLuxuryPlusSubscriptionsTotalPrice = createSelector(
  getLuxuryPlusSubscriptionItems,
  (items): number => sum(items, item => item.amount || 0),
)

export const getLuxuryPlusBreakdownView = createSelector(
  getLuxuryPlusSubscriptionItemView,
  getLuxuryPlusSubscriptionsTotalPrice,
  isStandaloneLuxuryPlusSubscription,
  (viewWithStatus, totalPrice, isStandalone): App.WithDataStatus<Array<App.Checkout.PriceBreakdownView>> => {
    if (viewWithStatus.data.length === 0) { return { hasRequiredData: viewWithStatus.hasRequiredData, data: [] } }
    const breakdownView = {
      title: `${LUXURY_PLUS.PROGRAM_NAME} Yearly Membership`,
      price: totalPrice,
      memberPrice: 0,
      additionalInfoText: [
        'One year membership',
        `Auto renews on ${intlFormatter(addYears(new Date(), 1), INTL_DMY_CASUAL_SHORT_FORMAT)}`,
      ],
      alwaysShowItemPrice: !isStandalone,
      items: [],
    }

    return {
      hasRequiredData: true,
      data: [breakdownView],
    }
  },
)

export const isLuxuryPlusSubscriptionInCheckout = createSelector(
  (state: App.State) => state.loyalty.member.subscription,
  isStandaloneLuxuryPlusSubscription,
  (subscription, isStandaloneCheckout): boolean => {
    return isStandaloneCheckout || !!(subscription.item && subscription.item.status === 'ACTIVE')
  },
)
